import { SET_CONFIG, RESET_CONFIG, SET_LOCAL_SETTINGS, SET_REMOTE_SETTINGS } from '../actionTypes/config';
import { RESET } from '../actionTypes/common';
import { PRECHAT_CAPTURE_TEMPLATES } from '../constants/prechat-capture';
import { computeColorsMetadata } from '../utils/config';

const INITIAL_STATE = {
    appId: '',
    fixedIntroPane: false,
    configBaseUrl: null,
    apiBaseUrl: null,
    integrationId: '',
    integrations: [],
    imageUploadEnabled: true,
    menuItems: {},
    soundNotificationEnabled: true,
    integrationOrder: null,
    businessName: '',
    businessIconUrl: '',
    backgroundImageUrl: '',
    displayStyle: '',
    buttonIconUrl: null,
    buttonWidth: 0,
    buttonHeight: 0,
    colors: {},
    isAppActive: false,
    prechatCapture: {},
};

const COLORS = ['brandColor', 'accentColor', 'linkColor'];

function pickNonEmptyColors(settings) {
    const result = {};

    for (const c of COLORS) {
        if (settings[c]) {
            result[c] = settings[c];
        }
    }

    return result;
}

function getFirstBooleanValue(...values) {
    return values.find((value) => typeof value === 'boolean');
}

function getFirstDefinedString(other, current) {
    return other || other === null || other === '' ? other : current;
}

/**
 * Returns the SDK config, where locally defined user settings (`state`) take precedence over the server-side ones (`settings`).
 * This reducing happens after `reduceWithLocalSettings`.
 *
 * @param {Object} state local SDK configuration defined by the user
 * @param {Object} settings SDK configuration fetched from the server
 */
function reduceWithRemoteSettings(state, settings = {}) {
    let integrationOrder = state.integrationOrder;

    if (!Array.isArray(integrationOrder) && Array.isArray(settings.integrationOrder)) {
        integrationOrder = [...settings.integrationOrder];
    }

    /**
     * This function returns the final prechat capture template and fields to be used.
     * In order of priority: local fields, local non-default template, server fields, server non-default template, default template
     *
     * @param {Object} localPrechat Prechat capture settings defined locally
     * @param {Object} serverPrechat Prechat Capture settings defined server-side
     */
    const getPrechatCaptureTemplateAndFields = (localPrechat, serverPrechat) => {
        if (localPrechat.fields) {
            return {
                template: undefined,
                fields: localPrechat.fields,
            };
        }

        if (localPrechat.template) {
            return {
                template: localPrechat.template,
                fields: serverPrechat.templateDefinitions[localPrechat.template],
            };
        }

        if (serverPrechat.fields) {
            return {
                template: undefined,
                fields: serverPrechat.fields,
            };
        }

        if (serverPrechat.template) {
            return {
                template: serverPrechat.template,
                fields: serverPrechat.templateDefinitions[serverPrechat.template],
            };
        }

        return {
            template: PRECHAT_CAPTURE_TEMPLATES.NameEmail,
            fields:
                (serverPrechat.templateDefinitions &&
                    serverPrechat.templateDefinitions[PRECHAT_CAPTURE_TEMPLATES.NameEmail]) ||
                undefined,
        };
    };

    const statePrechatCapture = state.prechatCapture || {};
    const settingsPrechatCapture = settings.prechatCapture || {};

    return {
        ...state,
        integrationOrder,
        displayStyle: state.displayStyle || settings.displayStyle,
        buttonIconUrl: state.buttonIconUrl || settings.buttonIconUrl,
        buttonWidth: state.buttonWidth || settings.buttonWidth,
        buttonHeight: state.buttonHeight || settings.buttonHeight,
        businessName: state.businessName || settings.businessName,
        businessIconUrl: state.businessIconUrl || settings.businessIconUrl,
        backgroundImageUrl: state.backgroundImageUrl || settings.backgroundImageUrl,
        fixedIntroPane: state.fixedIntroPane || settings.fixedIntroPane,
        colors: computeColorsMetadata({
            ...pickNonEmptyColors(settings),
            ...pickNonEmptyColors(state.colors),
        }),
        prechatCapture: {
            enabled: getFirstBooleanValue(statePrechatCapture.enabled, settingsPrechatCapture.enabled, false),
            enableEmailLinking: getFirstBooleanValue(
                statePrechatCapture.enableEmailLinking,
                settingsPrechatCapture.enableEmailLinking,
                true,
            ),
            avatarUrl: getFirstDefinedString(statePrechatCapture.avatarUrl, settingsPrechatCapture.avatarUrl),
            ...getPrechatCaptureTemplateAndFields(statePrechatCapture, settingsPrechatCapture),
        },
    };
}

/**
 * Returns the SDK config, where locally defined user settings (`settings`) take precedence over the default ones (`state`).
 * This reducing happens before `reduceWithRemoteSettings`.
 *
 * @param {Object} state SDK configuration containing the default values
 * @param {Object} settings SDK configuration defined by the user
 */
function reduceWithLocalSettings(state, settings = {}) {
    const statePrechatCapture = state.prechatCapture || {};
    const settingsPrechatCapture = settings.prechatCapture || {};

    return {
        ...state,
        integrationOrder: Array.isArray(settings.integrationOrder)
            ? [...settings.integrationOrder]
            : state.integrationOrder,
        displayStyle: settings.displayStyle || state.displayStyle,
        buttonIconUrl: settings.buttonIconUrl || state.buttonIconUrl,
        buttonWidth: settings.buttonWidth || state.buttonWidth,
        buttonHeight: settings.buttonHeight || state.buttonHeight,
        businessName: settings.businessName || state.businessName,
        businessIconUrl: settings.businessIconUrl || state.businessIconUrl,
        backgroundImageUrl: settings.backgroundImageUrl || state.backgroundImageUrl,
        fixedIntroPane: settings.fixedIntroPane || state.fixedIntroPane,
        colors: computeColorsMetadata({
            ...pickNonEmptyColors(state.colors),
            ...pickNonEmptyColors(settings),
        }),
        prechatCapture: {
            enabled: getFirstBooleanValue(settingsPrechatCapture.enabled, statePrechatCapture.enabled),
            enableEmailLinking: getFirstBooleanValue(
                settingsPrechatCapture.enableEmailLinking,
                statePrechatCapture.enableEmailLinking,
            ),
            avatarUrl: getFirstDefinedString(settingsPrechatCapture.avatarUrl, statePrechatCapture.avatarUrl),
            template: settingsPrechatCapture.fields
                ? undefined
                : settingsPrechatCapture.template || statePrechatCapture.template,
            fields: settingsPrechatCapture.fields || statePrechatCapture.fields || undefined,
        },
    };
}

export default function ConfigReducer(state = INITIAL_STATE, action) {
    switch (action.type) {
        case RESET:
        case RESET_CONFIG:
            return Object.assign({}, INITIAL_STATE);
        case SET_CONFIG:
            return {
                ...state,
                [action.key]: action.value,
            };
        case SET_LOCAL_SETTINGS:
            return reduceWithLocalSettings(state, action.settings);
        case SET_REMOTE_SETTINGS:
            return reduceWithRemoteSettings(state, action.settings);
        default:
            // TODO: remove this when the circular dependency exposed from running the reducers/config.spec test suite is fixed
            if (!state) {
                return {
                    fixedIntroPane: false,
                    configBaseUrl: null,
                    apiBaseUrl: null,
                    integrations: [],
                    imageUploadEnabled: true,
                    menuItems: {},
                    soundNotificationEnabled: true,
                    integrationOrder: null,
                    businessName: '',
                    businessIconUrl: '',
                    backgroundImageUrl: '',
                    displayStyle: '',
                    buttonIconUrl: null,
                    buttonWidth: 0,
                    buttonHeight: 0,
                    colors: {},
                    isAppActive: false,
                    prechatCapture: {},
                };
            }
            return state;
    }
}
